// Phase 20 · Events polling hook (30s + Visibility API pause).
import { useEffect, useRef, useState, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 30000;

/**
 * Polls a given URL every 30 seconds when the tab is visible.
 * Automatically pauses when the tab is hidden (Visibility API), per Fase 19/20 spec.
 * @param {string|null} url  Full URL to poll (returning JSON). If null → disabled.
 * @param {object}      opts { enabled, intervalMs, credentials }
 */
export default function useEventPolling(url, opts = {}) {
    const { enabled = true, intervalMs = POLL_MS, credentials = "include" } = opts;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const timerRef = useRef(null);
    const inFlightRef = useRef(false);

    const doFetch = useCallback(async () => {
        if (!url || document.hidden || inFlightRef.current) return;
        inFlightRef.current = true;
        try {
            const r = await fetch(url, { credentials });
            if (r.ok) {
                const d = await r.json();
                setData(d);
                setError(null);
            } else if (r.status === 503) {
                setError({ code: 503, disabled: true });
            } else {
                setError({ code: r.status });
            }
        } catch (e) {
            setError({ code: 0, message: String(e) });
        } finally {
            inFlightRef.current = false;
        }
    }, [url, credentials]);

    useEffect(() => {
        if (!enabled || !url) {
            if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
            return;
        }
        setLoading(true);
        doFetch().finally(() => setLoading(false));
        timerRef.current = setInterval(doFetch, intervalMs);
        const onVis = () => { if (!document.hidden) doFetch(); };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [enabled, url, intervalMs, doFetch]);

    return { data, loading, error, refetch: doFetch };
}

export { API };
